/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, Modal } from 'antd';
import { useState } from 'react';

import { ApiError, Subtenant } from '@/client';
import { validationErrors } from '@/components/Forms/validationErrors';
import { useMessage } from '@/hooks/message';
import { useUpdateSubtenantMutation } from '@/hooks/mutation/useUpdateSubtenantMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { SubtenantForm, SubtenantFormFields } from '@/pages/Subtenants/SubtenantForm';
import { getValidationMessage } from '@/utils/errors';

export type UpdateSubtenantModalProps = {
  subtenant: Subtenant | undefined;
  onClose: () => void;
};

export function UpdateSubtenantModal({ subtenant, onClose }: UpdateSubtenantModalProps) {
  const { t } = useMimicTranslation('subtenants');
  const [form] = Form.useForm<SubtenantFormFields>();

  const [message] = useMessage();
  const [updateIsPending, setUpdateIsPending] = useState(false);

  const { mutate: updateSubtenantMutation } = useUpdateSubtenantMutation({
    onError: (error, _) => {
      setUpdateIsPending(false);
      if (error instanceof ApiError) {
        const newMessage = getValidationMessage(error, 'name');
        form.setFields(validationErrors<Partial<SubtenantFormFields>>({ name: newMessage }));
      } else {
        message.error(t('subtenantUpdateError', subtenant));
      }
    },
    onSuccess: (updatedSubtenant) => {
      message.success(t('subtenantUpdated', { newName: updatedSubtenant.data.name, oldName: subtenant?.name }));
      closeEditDialog();
    },
    onMutate: () => {
      setUpdateIsPending(true);
    },
  });

  const onSubtenantUpdate = async () => {
    if (subtenant) {
      try {
        await form.validateFields();

        const name = form.getFieldValue('name');
        const description = form.getFieldValue('description');
        const parentSubtenantId = form.getFieldValue('subtenantId');
        const subtenantId = subtenant.id;
        const patchPayload = { name, description, subtenantId: parentSubtenantId };
        updateSubtenantMutation({ id: subtenantId, requestBody: patchPayload });
      } catch {
        // no op
      }
    }
  };

  const closeEditDialog = () => {
    setUpdateIsPending(false);
    onClose();
  };

  const onCancelEditSubtenant = () => {
    closeEditDialog();
  };

  return (
    <Modal
      key={subtenant?.id}
      open={Boolean(subtenant)}
      onCancel={onCancelEditSubtenant}
      onOk={onSubtenantUpdate}
      cancelText={t('editSubtenant.cancelButton')}
      cancelButtonProps={{ disabled: updateIsPending }}
      okText={t('editSubtenant.submitButton')}
      okButtonProps={{ loading: updateIsPending, htmlType: 'submit' }}
      title={t('editSubtenant.title')}
    >
      <SubtenantForm subtenant={subtenant} form={form} loading={updateIsPending} />
    </Modal>
  );
}
